// Order pieces within a showcase block. Pinned numeric (1..N) and
// 'last' positions are honored first; the remaining unpinned pieces
// are slotted with placement preferences (beginning/middle/end) and
// alternated between competition and non-competition entries.
//
// This same logic lives inline in several views (Admin/IABFPieces,
// Admin/ShowRunner, Admin/ShowcaseGaps, IABFDashboard/ShowcaseLineup);
// pull from here instead of adding a fifth copy.
export function orderBlockPieces (pieces) {
  if (!pieces.length) return []
  const pinnedNumeric = pieces
    .filter(p => Number.isFinite(p.showcaseOrder) && p.showcaseOrder >= 1)
    .slice()
    .sort((a, b) => a.showcaseOrder - b.showcaseOrder)
  const pinnedLast = pieces.filter(p => p.showcaseOrder === 'last')
  const unpinned = pieces.filter(p => !pinnedNumeric.includes(p) && !pinnedLast.includes(p))
  const N = pieces.length
  const slots = new Array(N).fill(null)
  for (const p of pinnedNumeric) {
    let target = Math.max(1, Math.min(p.showcaseOrder, N)) - 1
    while (target < N && slots[target] !== null) target += 1
    if (target >= N) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  for (const p of pinnedLast) {
    let target = N - 1
    while (target >= 0 && slots[target] !== null) target -= 1
    if (target === -1) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of unpinned) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const half = Math.ceil(groups.unspecified.length / 2)
  const placementOrder = [
    ...groups.beginning,
    ...groups.unspecified.slice(0, half),
    ...groups.middle,
    ...groups.unspecified.slice(half),
    ...groups.end
  ]
  const compQueue = placementOrder.filter(isComp)
  const nonQueue = placementOrder.filter(p => !isComp(p))
  for (let i = 0; i < N; i++) {
    if (slots[i] !== null) continue
    let prevType = null
    for (let j = i - 1; j >= 0; j--) {
      if (slots[j] !== null) { prevType = isComp(slots[j]) ? 'comp' : 'non'; break }
    }
    let nextType = null
    if (prevType === null) {
      for (let j = i + 1; j < N; j++) {
        if (slots[j] !== null) { nextType = isComp(slots[j]) ? 'comp' : 'non'; break }
      }
    }
    let wantComp
    if (prevType === 'comp') wantComp = false
    else if (prevType === 'non') wantComp = true
    else if (nextType === 'comp') wantComp = false
    else if (nextType === 'non') wantComp = true
    else wantComp = compQueue.length >= nonQueue.length
    if (wantComp && compQueue.length) slots[i] = compQueue.shift()
    else if (!wantComp && nonQueue.length) slots[i] = nonQueue.shift()
    else if (compQueue.length) slots[i] = compQueue.shift()
    else if (nonQueue.length) slots[i] = nonQueue.shift()
  }
  return slots
}

// Build a map of piece-id → "B-10" style lineup code. Each piece's
// position is its 1-indexed slot within its showcase block, derived
// from orderBlockPieces. Pieces without a showcaseBlock are skipped.
export function buildLineupCodeMap (allPieces) {
  const byBlock = new Map()
  for (const p of allPieces) {
    const block = p.showcaseBlock
    if (!block) continue
    if (!byBlock.has(block)) byBlock.set(block, [])
    byBlock.get(block).push(p)
  }
  const codeById = new Map()
  for (const [block, list] of byBlock) {
    const ordered = orderBlockPieces(list)
    ordered.forEach((p, idx) => {
      if (p && p.id) codeById.set(p.id, `${block}-${idx + 1}`)
    })
  }
  return codeById
}

// Sort key for pieces that are not yet in the showcase sequence
// (unassigned / pending). Large enough that a typical show never
// reaches it, so vue-good-table can still sort numerically.
export const SHOWCASE_UNPLACED_SORT_BASE = 10000

/**
 * Flatten the full show (corps slots + solo blocks) into one sequence.
 * Intermission is skipped. Each item is either
 *   { kind: 'corps', name } or { kind: 'piece', piece, block }.
 *
 * `piecesByBlock` is { [block]: piece[] }; pieces inside a block are
 * ordered with orderBlockPieces.
 */
export function flattenShowcaseSequence (structure, piecesByBlock) {
  const items = []
  if (!Array.isArray(structure)) {
    return items
  }
  const byBlock = piecesByBlock || {}
  for (const slot of structure) {
    if (!slot || typeof slot !== 'object') {
      continue
    }
    if (slot.type === 'corps') {
      items.push({ kind: 'corps', name: slot.name || '' })
      continue
    }
    if (slot.type === 'soloBlock' && slot.block) {
      const ordered = orderBlockPieces(byBlock[slot.block] || [])
      for (const piece of ordered) {
        if (piece) {
          items.push({ kind: 'piece', piece, block: slot.block })
        }
      }
    }
  }
  return items
}
